import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmSheet,
  DataTable,
  FormField,
  Input,
  MetricCard,
  PageHeader,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@jod/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { FormEvent } from "react";
import { useState } from "react";
import {
  useCreateOwnerAccount,
  usePlatformOwnerAccount,
  usePlatformVenue,
  useResetOwnerCredentials,
  useSetVenueActive,
} from "../../features/platform/queries.ts";
import type {
  OwnerAccountCredentials,
  PlatformAuditEntry,
  PlatformBranch,
} from "../../features/platform/types.ts";

export const Route = createFileRoute("/_app/venues/$venueId")({ component: VenueDetailScreen });

function VenueDetailScreen() {
  const { venueId } = Route.useParams();
  const { data: venue, isLoading, error } = usePlatformVenue(venueId);
  const setActive = useSetVenueActive();

  if (isLoading) return <p className="text-muted-foreground text-sm">Loading…</p>;
  if (error) {
    return (
      <p role="alert" className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
        {error.message}
      </p>
    );
  }
  if (!venue) return null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={venue.name}
        subtitle={venue.nameTh ?? undefined}
        actions={
          <div className="flex items-center gap-3">
            <Badge variant={venue.status === "active" ? "default" : "destructive"}>
              {venue.status}
            </Badge>
            <Button
              variant={venue.status === "active" ? "outline" : "default"}
              size="sm"
              disabled={setActive.isPending}
              onClick={() =>
                setActive.mutate({ venueId: venue.id, active: venue.status !== "active" })
              }
            >
              {venue.status === "active" ? "Suspend venue" : "Reactivate venue"}
            </Button>
          </div>
        }
      />
      <p className="text-muted-foreground text-sm">
        <Link to="/venues" className="text-primary">
          ← All venues
        </Link>{" "}
        · {venue.venueType} · {venue.timezone}
        {venue.address ? ` · ${venue.address}` : ""}
      </p>
      <div className="grid gap-4 sm:grid-cols-3">
        <MetricCard label="Branches" value={venue.branchCount} />
        <MetricCard label="Staff accounts" value={venue.staffCount} />
        <MetricCard
          label="Open sessions"
          value={venue.openSessionCount}
          tone={venue.openSessionCount ? "info" : "neutral"}
        />
      </div>
      <OwnerAccountCard venueId={venue.id} />
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg">Branches</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable<PlatformBranch>
            rows={venue.branches}
            getRowKey={(row) => row.id}
            empty={<p className="text-muted-foreground text-sm">No branches.</p>}
            columns={[
              { key: "name", header: "Branch", cell: (row) => row.name },
              { key: "address", header: "Address", cell: (row) => row.address ?? "—" },
              { key: "tz", header: "Timezone", cell: (row) => row.timezone },
              {
                key: "active",
                header: "Status",
                cell: (row) => (
                  <Badge variant={row.isActive ? "default" : "destructive"}>
                    {row.isActive ? "active" : "inactive"}
                  </Badge>
                ),
              },
            ]}
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg">Recent audit log</CardTitle>
          <p className="text-muted-foreground text-sm">
            Viewing this page is itself audited (FR-SADMIN-003).
          </p>
        </CardHeader>
        <CardContent>
          <DataTable<PlatformAuditEntry>
            rows={venue.recentAuditLog}
            getRowKey={(row) => row.id}
            empty={<p className="text-muted-foreground text-sm">No audit entries yet.</p>}
            columns={[
              { key: "action", header: "Action", cell: (row) => row.action },
              { key: "actor", header: "Actor", cell: (row) => row.actorType },
              {
                key: "at",
                header: "When",
                cell: (row) => new Date(row.createdAt).toLocaleString(),
              },
            ]}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function OwnerAccountCard({ venueId }: { venueId: string }) {
  const ownerAccount = usePlatformOwnerAccount(venueId);
  const createOwnerAccount = useCreateOwnerAccount(venueId);
  const resetOwnerCredentials = useResetOwnerCredentials(venueId);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<OwnerAccountCredentials | null>(null);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const accountMissing =
    ownerAccount.error instanceof Error &&
    /not found|OWNER_ACCOUNT_NOT_FOUND/i.test(ownerAccount.error.message);

  function handleGenerate(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setFormError(null);
    createOwnerAccount.mutate(
      { displayName, email: email.trim() || undefined },
      {
        onSuccess: (result) => {
          setCredentials(result);
          setDisplayName("");
          setEmail("");
        },
        onError: (err) => setFormError(err.message),
      },
    );
  }

  function handleResetConfirm(): void {
    setFormError(null);
    resetOwnerCredentials.mutate(
      { reason: "operator_reset" },
      {
        onSuccess: (result) => {
          setCredentials(result);
          setConfirmResetOpen(false);
        },
        onError: (err) => setFormError(err.message),
      },
    );
  }

  async function copyCredentials(): Promise<void> {
    if (!credentials) return;
    const text = `Email: ${credentials.email}\nPassword: ${credentials.generatedPassword}`;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg">Owner account</CardTitle>
          <p className="text-muted-foreground text-sm">
            Generate bootstrap credentials for the venue owner (FR-SADMIN-011). Password is shown
            once — deliver offline.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {ownerAccount.isLoading ? (
            <p className="text-muted-foreground text-sm">Checking owner account…</p>
          ) : null}

          {ownerAccount.data ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <p className="text-muted-foreground text-xs">Display name</p>
                <p className="font-medium text-sm">{ownerAccount.data.displayName}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Email (masked)</p>
                <p className="font-medium text-sm">{ownerAccount.data.email}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Status</p>
                <Badge variant={ownerAccount.data.status === "active" ? "default" : "destructive"}>
                  {ownerAccount.data.status}
                </Badge>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Must change password</p>
                <p className="font-medium text-sm">
                  {ownerAccount.data.mustChangePassword ? "Yes" : "No"}
                </p>
              </div>
              <div className="sm:col-span-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={resetOwnerCredentials.isPending}
                  onClick={() => setConfirmResetOpen(true)}
                >
                  Reset credentials
                </Button>
              </div>
            </div>
          ) : null}

          {accountMissing ? (
            <form className="grid gap-4 sm:grid-cols-2" onSubmit={handleGenerate}>
              <FormField id="owner-display-name" label="Display name">
                <Input
                  id="owner-display-name"
                  required
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                />
              </FormField>
              <FormField id="owner-email" label="Email (optional)">
                <Input
                  id="owner-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="Server generates if omitted"
                />
              </FormField>
              <div className="flex items-end sm:col-span-2">
                <Button type="submit" disabled={createOwnerAccount.isPending}>
                  {createOwnerAccount.isPending ? "Generating…" : "Generate owner account"}
                </Button>
              </div>
            </form>
          ) : null}

          {ownerAccount.error && !accountMissing ? (
            <p role="alert" className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
              {ownerAccount.error.message}
            </p>
          ) : null}

          {formError ? (
            <p role="alert" className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
              {formError}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Sheet
        open={credentials !== null}
        onOpenChange={(open) => {
          if (!open) setCredentials(null);
        }}
      >
        <SheetContent side="bottom" className="max-h-[85dvh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>One-time owner credentials</SheetTitle>
            <SheetDescription>
              Copy these credentials now. The password cannot be retrieved again.
            </SheetDescription>
          </SheetHeader>
          {credentials ? (
            <div className="space-y-4 px-4 pb-6">
              <div className="rounded-md bg-muted p-4 font-mono text-sm">
                <p>
                  <span className="text-muted-foreground">Email:</span> {credentials.email}
                </p>
                <p className="mt-2">
                  <span className="text-muted-foreground">Password:</span>{" "}
                  {credentials.generatedPassword}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={() => void copyCredentials()}>
                  {copied ? "Copied" : "Copy to clipboard"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setCredentials(null)}>
                  Done
                </Button>
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      <ConfirmSheet
        open={confirmResetOpen}
        onOpenChange={setConfirmResetOpen}
        title="Reset owner credentials?"
        description="This revokes active owner sessions and issues a new one-time password."
        confirmLabel={resetOwnerCredentials.isPending ? "Resetting…" : "Reset credentials"}
        destructive
        onConfirm={handleResetConfirm}
      />
    </>
  );
}
